import React, { useState, useEffect } from 'react';
import { X, Send, Calendar, Users, Eye, Mail, MessageSquare, Bold, Italic, List, Link as LinkIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';

const CampaignForm = ({ 
  show, 
  campaignType: initialCampaignType, 
  editingCampaign, 
  onClose, 
  onSave 
}) => {
  const [campaignType, setCampaignType] = useState(initialCampaignType || 'email');
  const [formData, setFormData] = useState({
    title: '',
    subject: '',
    html_content: '',
    message: '',
    recipient_type: 'all_members',
    custom_recipients: '',
    scheduled_for: '',
    track_opens: true,
    track_clicks: true
  });
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load editing campaign data
  useEffect(() => {
    if (editingCampaign) {
      setCampaignType(editingCampaign.type);
      setFormData({
        title: editingCampaign.title || '',
        subject: editingCampaign.subject || '',
        html_content: editingCampaign.html_content || '',
        message: editingCampaign.message || '',
        recipient_type: editingCampaign.recipient_type || 'all_members',
        custom_recipients: editingCampaign.custom_recipients?.join(', ') || '',
        scheduled_for: editingCampaign.scheduled_for || '',
        track_opens: editingCampaign.track_opens ?? true,
        track_clicks: editingCampaign.track_clicks ?? true
      });
    }
  }, [editingCampaign]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (campaignType === 'email') {
      if (!formData.subject.trim()) {
        newErrors.subject = 'Subject is required';
      }
      if (!formData.html_content.trim()) {
        newErrors.html_content = 'Email content is required';
      }
    } else {
      if (!formData.message.trim()) {
        newErrors.message = 'SMS message is required';
      }
      if (formData.message.length > 1600) {
        newErrors.message = 'SMS message must be 1600 characters or less';
      }
    }

    if (formData.recipient_type === 'custom' && !formData.custom_recipients.trim()) {
      newErrors.custom_recipients = 'Please enter at least one recipient';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (action = 'draft') => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      const campaignData = {
        ...formData,
        type: campaignType,
        action, // 'draft', 'schedule', or 'send'
        custom_recipients: formData.custom_recipients 
          ? formData.custom_recipients.split(',').map(r => r.trim()).filter(r => r)
          : null
      };

      await onSave(campaignData);
    } catch (error) {
      console.error('Campaign submission error:', error);
      setErrors({ general: error.message || 'Failed to save campaign. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-gray-900 rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl border border-gray-700 animate-in slide-in-from-bottom duration-300">
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex items-center justify-between bg-gradient-to-r from-gray-900 to-gray-800">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              {editingCampaign ? (
                <>Edit Campaign</>
              ) : (
                <>
                  <Send className="w-6 h-6 text-amber-500" />
                  Create New Campaign
                </>
              )}
            </h2>
            <p className="text-sm text-gray-400 mt-1">
              {campaignType === 'email' 
                ? 'Create an email campaign with rich content and tracking'
                : 'Create an SMS campaign for quick announcements'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-white transition p-2 hover:bg-gray-800 rounded-lg disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-900">
          <div className="space-y-6">
            {/* Campaign Type Selector */}
            {!editingCampaign && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Campaign Type *
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setCampaignType('email')}
                    disabled={isSubmitting}
                    className={`p-6 border-2 rounded-xl transition-all ${
                      campaignType === 'email'
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-gray-700 hover:border-gray-600 bg-gray-800'
                    }`}
                  >
                    <Mail className={`w-10 h-10 mx-auto mb-3 ${
                      campaignType === 'email' ? 'text-amber-500' : 'text-gray-400'
                    }`} />
                    <p className={`font-semibold text-lg ${
                      campaignType === 'email' ? 'text-amber-500' : 'text-white'
                    }`}>
                      Email Campaign
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Rich HTML content with tracking
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setCampaignType('sms')}
                    disabled={isSubmitting}
                    className={`p-6 border-2 rounded-xl transition-all ${
                      campaignType === 'sms'
                        ? 'border-amber-500 bg-amber-500/10'
                        : 'border-gray-700 hover:border-gray-600 bg-gray-800'
                    }`}
                  >
                    <MessageSquare className={`w-10 h-10 mx-auto mb-3 ${
                      campaignType === 'sms' ? 'text-amber-500' : 'text-gray-400'
                    }`} />
                    <p className={`font-semibold text-lg ${
                      campaignType === 'sms' ? 'text-amber-500' : 'text-white'
                    }`}>
                      SMS Campaign
                    </p>
                    <p className="text-xs text-gray-400 mt-2">
                      Quick text announcements
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Campaign Title */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Campaign Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Monthly Newsletter - January 2025"
                disabled={isSubmitting}
                className={`w-full px-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition ${
                  errors.title ? 'border-red-500' : 'border-gray-700'
                }`}
              />
              {errors.title && (
                <p className="text-red-400 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            {/* Email Subject (Email only) */}
            {campaignType === 'email' && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Subject *
                </label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => handleInputChange('subject', e.target.value)}
                  placeholder="e.g., Exciting Updates from Success Club"
                  disabled={isSubmitting}
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition ${
                    errors.subject ? 'border-red-500' : 'border-gray-700'
                  }`}
                />
                {errors.subject && (
                  <p className="text-red-400 text-sm mt-1">{errors.subject}</p>
                )}
              </div>
            )}

            {/* Email Content or SMS Message */}
            {campaignType === 'email' ? (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Email Content * (HTML supported)
                </label>
                <textarea
                  id="email-content-textarea"
                  value={formData.html_content}
                  onChange={(e) => handleInputChange('html_content', e.target.value)}
                  placeholder="Write your email content here. HTML tags are supported."
                  rows={12}
                  disabled={isSubmitting}
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition font-mono text-sm ${
                    errors.html_content ? 'border-red-500' : 'border-gray-700'
                  }`}
                />
                {errors.html_content && (
                  <p className="text-red-400 text-sm mt-1">{errors.html_content}</p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2 flex justify-between">
                  <span>SMS Message *</span>
                  <span className="text-gray-500 text-xs">
                    {formData.message.length} / 1600 characters
                  </span>
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  placeholder="Write your SMS message here (max 1600 characters)..."
                  rows={8}
                  disabled={isSubmitting}
                  className={`w-full px-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition ${
                    errors.message ? 'border-red-500' : 'border-gray-700'
                  }`}
                />
                {errors.message && (
                  <p className="text-red-400 text-sm mt-1">{errors.message}</p>
                )}
              </div>
            )}

            {/* Recipients */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Recipients *
              </label>
              <select
                value={formData.recipient_type}
                onChange={(e) => handleInputChange('recipient_type', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
              >
                <option value="all_members">All Members</option>
                <option value="all_hosts">All Hosts</option>
                <option value="all_users">All Users (Members + Hosts)</option>
                <option value="custom">Custom Recipients</option>
              </select>

              {formData.recipient_type === 'custom' && (
                <div className="mt-3">
                  <input
                    type="text"
                    value={formData.custom_recipients}
                    onChange={(e) => handleInputChange('custom_recipients', e.target.value)}
                    placeholder="Enter email addresses or phone numbers, separated by commas"
                    disabled={isSubmitting}
                    className={`w-full px-4 py-3 bg-gray-800 border rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition ${
                      errors.custom_recipients ? 'border-red-500' : 'border-gray-700'
                    }`}
                  />
                  {errors.custom_recipients && (
                    <p className="text-red-400 text-sm mt-1">{errors.custom_recipients}</p>
                  )}
                </div>
              )}
            </div>

            {/* Schedule */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Schedule for Later (Optional)
              </label>
              <input
                type="datetime-local"
                value={formData.scheduled_for}
                onChange={(e) => handleInputChange('scheduled_for', e.target.value)}
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition"
              />
              <p className="text-xs text-gray-500 mt-1">
                Leave empty to save as draft or send immediately
              </p>
            </div>

            {/* Preview */}
            {campaignType === 'email' && formData.html_content && (
              <div>
                <Button
                  type="button"
                  onClick={() => setShowPreview(!showPreview)}
                  variant="outline"
                  className="w-full bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                  disabled={isSubmitting}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>

                {showPreview && (
                  <div className="mt-4 p-6 bg-gray-800 border border-gray-700 rounded-xl">
                    <h4 className="font-semibold text-white mb-3">Email Preview</h4>
                    <div className="mb-3 pb-3 border-b border-gray-700">
                      <p className="text-sm text-gray-400">
                        <strong className="text-gray-300">Subject:</strong> {formData.subject || '(No subject)'}
                      </p>
                    </div>
                    <div 
                      className="bg-white p-6 rounded-lg text-gray-900"
                      dangerouslySetInnerHTML={{ __html: formData.html_content }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-gray-700 bg-gray-800">
          {errors.general && (
            <div className="mb-4 p-4 bg-red-900/30 border border-red-500/50 rounded-lg">
              <p className="text-red-300 text-sm">{errors.general}</p>
            </div>
          )}
          
          <div className="flex justify-between items-center">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={isSubmitting}
              className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
            >
              Cancel
            </Button>

            <div className="flex gap-3">
              {!editingCampaign && (
                <Button
                  type="button"
                  onClick={() => handleSubmit('draft')}
                  variant="outline"
                  disabled={isSubmitting}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save as Draft'
                  )}
                </Button>
              )}

              {formData.scheduled_for && (
                <Button
                  type="button"
                  onClick={() => handleSubmit('schedule')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Scheduling...
                    </>
                  ) : (
                    <>
                      <Calendar className="w-4 h-4 mr-2" />
                      Schedule Campaign
                    </>
                  )}
                </Button>
              )}

              {!formData.scheduled_for && (
                <Button
                  type="button"
                  onClick={() => handleSubmit('send')}
                  className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white shadow-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingCampaign ? 'Updating...' : 'Sending...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      {editingCampaign ? 'Update & Send' : 'Send Now'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampaignForm;
